import { FC, useEffect, useMemo, useState } from 'react';
import { FormInstance } from 'antd';
import i18n from 'i18next';
import cn from 'classnames';

import { Filter, FilterItem, Filters, FiltersForm } from 'componets/ui';
import { DropDownComponent } from 'componets/common';
import { CheckBoxGroup, FormItem } from 'componets/form';

import { useAppDispatch, useAppSelector, useTranslate } from 'hooks';
import { FieldData, IFilter, IFilterValues } from 'models';
import { downloadTransactions } from 'features/statement/actions';
import Utils from 'utils/utils';

import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';

import s from '../index.module.scss';

type TransactionsFiltersProps = {
	form: FormInstance;
	initialValues: IFilterValues;
	values: IFilterValues;
};
export const TransactionsFilters: FC<TransactionsFiltersProps> = ({
	form,
	initialValues,
	values,
}) => {
	const t = useTranslate([
		'transactionTypeText',
		'projectIdFilterText',
		'downloadTransactionsText',
		'dateOfTransactionText',
	]);
	const dispatch = useAppDispatch();
	const transactionTypes = useAppSelector((state) => state.settings.globals.transactionTypes);

	const [fields, setFields] = useState<FieldData[]>([]);

	const transactions = useAppSelector((state) => state.statement.transactions);

	const [filters, setFilters] = useState([] as IFilter[]);
	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		let data: IFilter[] = [];

		if (values) {
			data = Utils.transformFilters(values, [], [], transactionTypesValues);
		}

		setFilters(data);
	}, [values]);

	const onDownload = () => {
		dispatch(
			downloadTransactions({
				params: {
					startDate: values.minDateOfPurchase,
					endDate: values.maxDateOfPurchase,
					loanId: values.loanId,
					language: 'en',
					transactionType: values.transactionType,
				},
			}),
		);
	};

	const onResetFields = () => {
		form.setFieldsValue({
			transactionType: undefined,
			minDateOfPurchase: '',
			maxDateOfPurchase: '',
			loanId: '',
		});
	};

	return (
		<div>
			<div className={s.reportsFilters}>
				<div>
					{transactions.length > 0 && (
						<div role="button" className="white-btn" onClick={onDownload}>
							<DownloadIcon />
							{t.downloadTransactionsText}
						</div>
					)}
				</div>

				<Filter
					onResetFields={onResetFields}
					isOpen={isOpen}
					onOpen={() => setIsOpen(true)}
					onClose={() => setIsOpen(false)}
				>
					<FiltersForm
						filters={filters}
						onFinish={() => setIsOpen(false)}
						setFields={setFields}
						form={form}
						initialValues={{ ...initialValues }}
						fields={fields}
					>
						<FilterItem name="loanId" title={t.projectIdFilterText} isField />
						<FilterItem
							name="transationType"
							title={t.transactionTypeText}
							isDropdown
							Dropdown={() => (
								<div className={cn('selectType', s.types)}>
									<DropDownComponent
										dropdownRender={() => <ReportsTypesDropdown values={transactionTypes} />}
										label={t.transactionTypeText}
									/>
								</div>
							)}
						/>

						<FilterItem
							name="date"
							title={t.dateOfTransactionText}
							isDate
							minDate={values?.minDateOfPurchase}
							maxDate={values?.maxDateOfPurchase}
						/>
					</FiltersForm>
				</Filter>
			</div>
			{filters.length > 0 && (
				<>
					<div className={s.line} />
					<Filters filters={filters} onReset={onResetFields} form={form} values={values} />
				</>
			)}
		</div>
	);
};

const ReportsTypesDropdown: FC<{
	values: { [key: string]: string };
}> = ({ values }) => {
	const options = useMemo(
		() =>
			Object.keys(values)
				.map((item) => {
					return { value: item, label: transactionTypesValues[item] };
				})
				.filter((item) => item.value !== '0'),
		[values],
	);

	return (
		<div className="dropdownContainer">
			<div className={s.checkboxes}>
				<FormItem name="transactionType">
					<CheckBoxGroup options={options} />
				</FormItem>
			</div>
		</div>
	);
};

const transactionTypesValues: { [key: string]: string } = {
	1: i18n.t('depositTransactionText'),
	2: i18n.t('withdrawalText'),
	3: i18n.t('principalRepaymentText'),
	4: i18n.t('interestPaymentText'),
	11: i18n.t('investmentText'),
	16: i18n.t('feesBonusesText'),
};
